import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	postMoyenPaiements,
	postMoyenPaiementsReset,
	deleteMoyenPaiement,
	deleteMoyenPaiementReset,
} from "../../../store/actions/autreActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"

// Modules métier exposés par les flags showDep / showAte / showLoc.
const MODULES = [
	{ key: "showDep", label: "Dépannage" },
	{ key: "showAte", label: "Atelier" },
	{ key: "showLoc", label: "Location" },
]

const styles = {
	toggleRow: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		cursor: "pointer",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	valueWrap: { display: "flex", alignItems: "center", gap: 8, flex: 1 },
	unit: { fontSize: 13, color: COLORS.textMuted, minWidth: 30 },
	modules: { display: "flex", flexWrap: "wrap", gap: 16, flex: 1 },
	moduleLabel: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 13,
		color: COLORS.text,
		cursor: "pointer",
	},
}

// Édition d'un moyen de paiement (modal V2). Réutilise l'infra Redux existante :
// postMoyenPaiements (création/maj) et deleteMoyenPaiement. Le backend attend un
// objet indexé { 0: {...} } et renvoie la liste fraîche → la table se met à jour
// toute seule via le reducer.
const MoyenPaiementModalV2 = ({ moyen, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.autreInfo.postMoyenPaiements)
	const deleteState = useSelector((s) => s.autreInfo.deleteMoyenPaiement)
	const isCreate = !moyen.id
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [form, setForm] = useState({
		nom: moyen.nom || "",
		value: moyen.value !== undefined ? String(moyen.value) : "0",
		isMult: moyen.isMult ? 1 : 0,
		apply: moyen.apply || false,
		showDep: moyen.showDep || false,
		showAte: moyen.showAte || false,
		showLoc: moyen.showLoc || false,
	})
	const update = (k, v) => setForm((p) => ({ ...p, [k]: v }))

	// Fermeture après une sauvegarde OU une suppression réussie.
	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postMoyenPaiementsReset())
			dispatch(deleteMoyenPaiementReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postMoyenPaiementsReset())
			dispatch(deleteMoyenPaiementReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	// Quitte le mode "confirmer" si la suppression échoue (moyen protégé).
	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	const invalid = !form.nom.trim() || isNaN(parseFloat(form.value))

	const save = () => {
		// id absent en création → on envoie un id qui n'existe pas (le backend
		// retombe alors sur un nouvel objet).
		const payload = { ...form, id: moyen.id || -1 }
		dispatch(postMoyenPaiements(JSON.stringify({ 0: payload })))
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteMoyenPaiement(JSON.stringify({ id: moyen.id })))
	}

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={isCreate ? "Nouveau moyen de paiement" : "Éditer le moyen de paiement"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={form.nom}
						onChange={(e) => update("nom", e.target.value)}
					/>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Type</label>
					<select
						style={gs.input}
						value={form.isMult}
						onChange={(e) =>
							update("isMult", parseInt(e.target.value))
						}
					>
						<option value={0}>Addition (CHF)</option>
						<option value={1}>Multiplication (%)</option>
					</select>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Valeur<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<div style={styles.valueWrap}>
						<input
							style={gs.input}
							type="number"
							step="any"
							value={form.value}
							onChange={(e) => update("value", e.target.value)}
						/>
						<span style={styles.unit}>
							{form.isMult ? "%" : "CHF"}
						</span>
					</div>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Applicable</label>
					<label style={styles.toggleRow}>
						<input
							type="checkbox"
							checked={form.apply}
							onChange={(e) => update("apply", e.target.checked)}
						/>
						{form.apply ? "Appliqué au calcul" : "Non appliqué"}
					</label>
				</div>
				<div style={{ ...gs.fieldRow, alignItems: "flex-start" }}>
					<label style={gs.label}>Modules</label>
					<div style={styles.modules}>
						{MODULES.map((m) => (
							<label key={m.key} style={styles.moduleLabel}>
								<input
									type="checkbox"
									checked={form[m.key]}
									onChange={(e) =>
										update(m.key, e.target.checked)
									}
								/>
								{m.label}
							</label>
						))}
					</div>
				</div>
			</div>
		</CustomModal>
	)
}

export default MoyenPaiementModalV2
